import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { endOfToday, startOfDay, subDays, subWeeks } from 'date-fns';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { meanObject, std } from 'lib_ts/classes/math.utilities';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { MlbSportId } from 'lib_ts/enums/mlb-stats-api/base.enum';
import {
  BuildPriority,
  PitchType,
  lookupPitchType,
  tooltipPitchType,
} from 'lib_ts/enums/pitches.enums';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import {
  IGroupedFires,
  IPitchPerfFilters,
  IPitchPerformanceFire,
} from 'lib_ts/interfaces/data/i-pitch-performance';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import {
  IMlbPitch,
  IMlbPitchExt,
} from 'lib_ts/interfaces/mlb-stats-api/i-pitch';
import { IMlbPlayer } from 'lib_ts/interfaces/mlb-stats-api/i-player';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { IAggregateMachineShotsDict } from 'lib_ts/interfaces/training/i-machine-shot';
import { compact, isEqual, max } from 'lodash';
import { AdminMachinesService } from 'services/admin/machines.service';
import { DataCollectionsService as DCService } from 'services/data-collections.service';
import { MlbStatsService } from 'services/mlb-stats.service';
import { ShotsService } from 'services/shots.service';
import { create } from 'zustand';

// default report shows last 100 shots from the previous week
const DEFAULT_NUM_SHOTS = 100;
const DEFAULT_DELTA_WEEKS = 1;

const MAX_STATS_BREAKS_TO_PLOT = 20;

const getPitchGroups = (pitches: IMlbPitchExt[], groups: IGroupedFires[]) => {
  const dict = ArrayHelper.groupBy(pitches, 'type');

  return Object.keys(dict)
    .sort()
    .map((mlbType) => {
      // Map MLB pitch type string to internal PitchType enum
      const pitchType = lookupPitchType(mlbType);
      // Find the reference pitch group for this pitch type
      const refPitch = groups.find((g) => g.pitch.type === pitchType);

      // Gather all valid break objects for this pitch type, up to the plotting limit
      const breaks = compact(
        (dict[mlbType] ?? [])
          // Only keep guids with no errors
          .filter((guid) => MlbStatsHelper.getErrors(guid).length === 0)
          // Convert each guid to its break representation (if available)
          .map((guid) => {
            const converted = MlbStatsHelper.convertToChars(
              guid,
              BuildPriority.Breaks,
              true
            );

            if (!converted.traj) {
              return undefined;
            }

            return TrajHelper.getBreaks(converted.traj);
          })
      ).filter((_, i) => i < MAX_STATS_BREAKS_TO_PLOT);

      // Compute the average break for this pitch type
      const avgBreak = meanObject(breaks);

      // Calculate the error (distance from avg) for each dim of break
      const errorsIn: ITrajektRefBreak[] = compact(
        breaks.map((m) => {
          if (!avgBreak) {
            return undefined;
          }

          const o: ITrajektRefBreak = {
            xInches: m.xInches - avgBreak.xInches,
            zInches: m.zInches - avgBreak.zInches,
          };

          return o;
        })
      );

      // Standard deviation of break errors
      const stdErrorsIn = Math.sqrt(
        Math.pow(std(errorsIn.map((m) => m.xInches)), 2) +
          Math.pow(std(errorsIn.map((m) => m.zInches)), 2)
      );

      // Compose the pitch group object for this pitch type
      const o: IPitchGroup = {
        pitchID: refPitch?._id ?? 'none', // fallback if not found
        type: pitchType,
        typeName: tooltipPitchType(pitchType),
        breaks: breaks,
        avgBreak: avgBreak,
        stdBreakErrorIn: breaks.length > 2 ? stdErrorsIn : undefined, // only if enough data
        maxBreakErrorIn: max(
          errorsIn.map((m) =>
            Math.sqrt(Math.pow(m.xInches, 2) + Math.pow(m.zInches, 2))
          )
        ),

        rgb: refPitch?.rgb ?? '100,100,100', // fallback color
        invisible: !refPitch, // mark as invisible if no reference pitch
      };

      // Return the constructed pitch group
      return o;
    })
    .sort((a, b) => {
      if (a.invisible === b.invisible) {
        // sort alphabetically
        return StringHelper.safeSort(a.typeName, b.typeName);
      }

      // put invisible stuff last
      return a.invisible ? 1 : -1;
    });
};

interface IGroupFilters {
  search: string;
  pitchType?: PitchType;
  onlyShowChecked: boolean;
}

// MARK: state interface
interface State {
  loading: boolean;

  groups: IGroupedFires[];

  // what is plotted + shown in metrics (before filtering for checked shots)
  visibleGroups: IGroupedFires[];

  //  only used for HWD to house the groups from the original RT
  refGroups: IGroupedFires[];

  // first group of each pitch type (for statsPitchGroups to use as ref)
  firstGroups: IGroupedFires[];

  // for filtering data from the server
  filters: IPitchPerfFilters;

  // for post-fetch group table filtering via PitchPerfGroupTable
  groupFilters: IGroupFilters;

  machineID: string;
  selectedPitchId: string;
  selectedMsHash: string;
  selectedShotIds: string[];

  showThresholds: boolean;

  aggDict: IAggregateMachineShotsDict;
}

// MARK: actions interface
interface Actions {
  readonly setFirstGroups: (groups: IGroupedFires[]) => void;
  readonly mergeFilters: (value: Partial<IPitchPerfFilters>) => void;
  readonly initialize: (machineId: string, ballType?: BallType) => void;
  readonly generate: () => Promise<void>;
  readonly generateHwDiagnostic: (hwd: IDataCollection) => Promise<void>;
  readonly reset: () => void;
}

interface IPitchGroup {
  pitchID: string;
  type: PitchType;
  typeName: string;

  breaks: ITrajektRefBreak[];
  avgBreak: ITrajektRefBreak | undefined;

  stdBreakErrorIn: number | undefined;
  maxBreakErrorIn: number | undefined;

  // color of the first (checked) pitch in the data with the same pitch type
  rgb: string;
  invisible: boolean;
}

interface IStatsApiState {
  statsDialog?: number;

  statsLoading: boolean;

  statsSeason?: number;
  statsLeague?: MlbSportId;

  statsPitcherPk?: number;

  // loaded and set whenever we have season, league, and pitcher specified
  statsPitchGroups: IPitchGroup[];
  cachedStatsPitches: IMlbPitch[];

  statsSeasonOptions: IOption[];
  statsLeagueOptions: IOption[];

  // for rendering the pitcher details like hand, team name, etc...
  statsPitchers: IMlbPlayer[];
}

interface IStatsApiActions {
  readonly setStatsDialog: (v: number | undefined) => void;
  readonly fetchStatsSeasons: () => void;
  readonly fetchStatsPitchers: () => void;
  readonly fetchStatsPitches: (
    groups: IGroupedFires[],
    useCached: boolean
  ) => void;

  readonly setStatsSeason: (value: number | undefined) => void;
  // * not currently used, assumed to be always MLB
  readonly setStatsLeague: (value: MlbSportId | undefined) => void;
  readonly setStatsPitcherPk: (value: number | undefined) => void;
}

type IPitchPerformanceStore = State &
  Actions &
  IStatsApiState &
  IStatsApiActions;

const generateDefaultFilters = (): IPitchPerfFilters => {
  const currEnd = endOfToday();
  const currStart = startOfDay(subWeeks(currEnd, DEFAULT_DELTA_WEEKS));

  const prevEnd = subDays(currStart, 1);
  const prevStart = startOfDay(subWeeks(prevEnd, DEFAULT_DELTA_WEEKS));

  const output: IPitchPerfFilters = {
    currPeriod: {
      start: currStart.toISOString(),
      end: currEnd.toISOString(),
    },
    prevPeriod: {
      start: prevStart.toISOString(),
      end: prevEnd.toISOString(),
    },
    shots: DEFAULT_NUM_SHOTS,
    // ensures that the ball_type changed detection works on init
    ball_type: undefined,
    training: null,
    includeLowConfidence: false,
  };

  return output;
};

const initialState: State & IStatsApiState = {
  loading: false,

  groups: [],
  visibleGroups: [],
  refGroups: [],
  firstGroups: [],

  filters: generateDefaultFilters(),
  machineID: '',
  selectedPitchId: '',
  selectedMsHash: '',
  selectedShotIds: [],

  groupFilters: {
    search: '',
    pitchType: undefined,
    onlyShowChecked: false,
  },

  showThresholds: false,

  statsLoading: false,

  statsSeason: undefined,
  statsLeague: MlbSportId.MLB,
  statsPitcherPk: undefined,

  statsSeasonOptions: [],
  statsLeagueOptions: [],

  statsPitchers: [],

  statsPitchGroups: [],
  cachedStatsPitches: [],

  aggDict: {},
};

const usePitchPerformanceStore = create<IPitchPerformanceStore>((set, get) => ({
  ...initialState,

  setFirstGroups: (groups) => {
    const dict = ArrayHelper.firstByFn(groups, (g) => g.pitch.type);
    set({ firstGroups: compact(Object.values(dict)) });
  },

  mergeFilters: async (value: Partial<IPitchPerfFilters>) => {
    const { filters: currentFilters, machineID } = get();

    const mergedFilters = { ...currentFilters, ...value };

    // because fetching the report is potentially expensive, we'll do a manual equality check here
    if (!isEqual(currentFilters, mergedFilters)) {
      // reload aggDict whenever ball type changes
      if (currentFilters.ball_type !== mergedFilters.ball_type) {
        set({ loading: true });

        const aggDict = await ShotsService.getInstance().getAggMatches({
          machineID: machineID,
          ball_type: mergedFilters.ball_type,
          includeLowConfidence: true,
          includeHitterPresent: false,
        });

        set({
          loading: false,
          aggDict,
        });
      }

      set({ filters: mergedFilters });
    }
  },

  initialize: (machineID: string, ballType?: BallType) => {
    const { mergeFilters } = get();

    set({ machineID });

    // init ball type filter with the current machine ball type
    mergeFilters({
      ball_type: ballType ?? initialState.filters.ball_type,
    });
  },

  generate: async () => {
    const { filters, machineID } = get();

    try {
      set({ loading: true });

      const results =
        await AdminMachinesService.getInstance().getPitchPerformance(
          machineID,
          filters
        );

      if (!results) {
        throw new Error('Server returned empty results');
      }

      set({
        groups: results.current,
        refGroups: results.previous,
        loading: false,
      });
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: `Unable to fetch pitch performance: ${e}`,
      });

      set({ loading: false });
    }
  },

  generateHwDiagnostic: async (hwd) => {
    try {
      if (!hwd.refID) {
        throw new Error(`HWD is missing a refID (i.e. RT ID)`);
      }

      set({ loading: true });

      const [hwdSummaries, rtSummaries] = await Promise.all(
        [hwd._id, hwd.refID].map((id) => DCService.getShotSummaries(id))
      );

      const pitchDict = ArrayHelper.firstBy(hwd.pitches ?? [], '_id');

      const [hwdGroups, rtGroups] = [hwdSummaries, rtSummaries].map(
        (summaries) => {
          const fires: IPitchPerformanceFire[] = [];

          summaries.forEach((summary) => {
            if (!summary.pitch) {
              return;
            }

            summary.shots.forEach((shot) => {
              const fire = MachinePerformanceHelper.getFire(shot, pitchDict);

              if (fire) {
                fires.push(fire);
              }
            });
          });

          return MachinePerformanceHelper.getFireGroup(fires);
        }
      );

      set({
        groups: hwdGroups,
        refGroups: rtGroups,
        loading: false,
      });
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: `Unable to fetch hardware diagnostic: ${e}`,
      });

      set({ loading: false });
    }
  },

  reset: () => set(initialState),

  setStatsDialog: (v) => set({ statsDialog: v }),

  fetchStatsSeasons: async () => {
    const { statsLeague } = get();

    if (!statsLeague) {
      return;
    }

    set({
      statsLoading: true,
    });

    const seasons = await MlbStatsService.getInstance().countGames({
      sportId: statsLeague,
    });

    seasons.sort((a, b) => (a.season > b.season ? -1 : 1));

    const options = seasons.map((m) => {
      const o: IOption = {
        label: m.season.toString(),
        value: m.season.toString(),
        disabled: m.total === 0,
      };

      return o;
    });

    set({
      statsLoading: false,
      statsSeasonOptions: options,
    });
  },

  fetchStatsPitchers: async () => {
    const { statsSeason, statsLeague } = get();

    if (!statsSeason) {
      return;
    }

    if (!statsLeague) {
      return;
    }

    set({
      statsLoading: true,
    });

    const teams = await MlbStatsService.getInstance().getTeams({
      season: statsSeason,
      sportId: statsLeague,
    });

    const players = await MlbStatsService.getInstance().getPlayers({
      season: statsSeason,
      sportId: statsLeague,
      // teams are just provided so we mixin details like for the colors on avatar bgs
      teams: teams,
    });

    set({
      statsLoading: false,
      statsPitchers: players,
    });
  },

  fetchStatsPitches: async (groups, useCached) => {
    if (useCached) {
      const { cachedStatsPitches } = get();
      set({
        statsPitchGroups: getPitchGroups(cachedStatsPitches, groups),
      });
      return;
    }

    const { statsSeason, statsLeague, statsPitcherPk } = get();

    set({ statsLoading: true });

    const statsPitches =
      statsSeason && statsLeague && statsPitcherPk
        ? await MlbStatsService.getInstance().getPitches({
            sportId: statsLeague,
            season: statsSeason,
            pitcherPk: statsPitcherPk,
          })
        : [];

    set({
      statsLoading: false,
      cachedStatsPitches: statsPitches,
      statsPitchGroups: getPitchGroups(statsPitches, groups),
    });
  },

  setStatsSeason: (value: number | undefined) => {
    set({ statsSeason: value });
  },
  setStatsLeague: (value: MlbSportId | undefined) => {
    set({ statsLeague: value });
  },
  setStatsPitcherPk: async (value: number | undefined) => {
    set({ statsPitcherPk: value });
  },
}));

export default usePitchPerformanceStore;
